import type {
  U3141ImportChainAnnotationsApplyBody,
  U3141ImportChainAnnotationsDryRunResponse,
} from '../../../U3141Service';
import { u3141ParseImportChainAnnotationsDryRunResponse } from '../../../tools/u3141ImportChainAnnotationsHelpers';

export interface U3141UseImportChainAnnotationsHandlersParams {
  doDryRunFn: (masterLotChainId: string, file: File) => Promise<{ data: unknown }>;
  doApplyFn: (
    masterLotChainId: string,
    body: U3141ImportChainAnnotationsApplyBody,
  ) => Promise<{ data: unknown }>;
  onDryRunSuccess?: (result: U3141ImportChainAnnotationsDryRunResponse) => void;
  onApplySuccess?: (jobId: string) => void;
  onError?: (errors: { error: unknown }) => void;
}

export interface U3141UseImportChainAnnotationsHandlersReturn {
  dryRunImportChainAnnotations: (masterLotChainId: string, file: File) => Promise<void>;
  applyImportChainAnnotations: (
    masterLotChainId: string,
    body: U3141ImportChainAnnotationsApplyBody,
  ) => Promise<void>;
}

function extractJobId(data: unknown): string {
  if (!data || typeof data !== 'object') return '';
  const row = data as Record<string, unknown>;
  const result = row.result && typeof row.result === 'object' ? (row.result as Record<string, unknown>) : row;
  const rawId = result.id ?? row.id;
  return typeof rawId === 'string' ? rawId : '';
}

export function useU3141ImportChainAnnotationsHandlers({
  doDryRunFn,
  doApplyFn,
  onDryRunSuccess,
  onApplySuccess,
  onError,
}: U3141UseImportChainAnnotationsHandlersParams): U3141UseImportChainAnnotationsHandlersReturn {
  const dryRunImportChainAnnotations = async (masterLotChainId: string, file: File) => {
    try {
      const response = await doDryRunFn(masterLotChainId, file);
      const parsed = u3141ParseImportChainAnnotationsDryRunResponse(response?.data);
      if (!parsed) {
        onError?.({ error: new Error('Invalid dry-run response') });
        return;
      }
      onDryRunSuccess?.(parsed);
    } catch (error) {
      onError?.({ error });
    }
  };

  const applyImportChainAnnotations = async (
    masterLotChainId: string,
    body: U3141ImportChainAnnotationsApplyBody,
  ) => {
    try {
      const response = await doApplyFn(masterLotChainId, body);
      const jobId = extractJobId(response?.data);
      onApplySuccess?.(jobId);
    } catch (error) {
      onError?.({ error });
    }
  };

  return { dryRunImportChainAnnotations, applyImportChainAnnotations };
}
